import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useMutation } from "@grandvisionhq/graphql";
import { useCartStorage, CART_STORAGE_KEY } from "@grandvisionhq/lib-utils-cart";
import { useIntl, useApiConfig, useFeatureToggles } from "@grandvisionhq/state";
import { monetate } from "@grandvisionhq/svc-monetate";
import { useDispatchTrackEvent, useDispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { isNotNull } from "@grandvisionhq/utils";
import { errorsMapping } from "../../mappers/mappers/error-codes.js";
import { mapDraftLineItemToServer } from "../../mappers/server/mappers/server/line-item.js";
import { toVisualLineItem } from "../../mappers/view/visual-line-item/mappers/view/visual-line-item/cart-line-item.js";
import { dispatchMiniCartEvent } from "../../modules/MiniCart/utils/modules/MiniCart/utils/event.js";
import { useCartSettings } from "../../context/CartSettings/context/CartSettings/context.js";
import { getSolvableValidationErrors } from "../../utils/utils/get-solvable-validation-errors.js";
import { getValidationErrorCodes } from "../../utils/utils/get-validation-error-codes.js";
import { getAddedLineItems } from "../../utils/utils/get-added-line-items.js";
import { lineItemDataMapper } from "../../utils/utils/line-item-data-mapper.js";
import { mapLineItemToDataLayerEvent } from "../../mappers/analytics/data-layer/mappers/analytics/data-layer/line-item-to-data-layer-event.js";
import { mapVisualLineItemProductToTealiumProduct } from "../../mappers/analytics/tealium/mappers/analytics/tealium/line-item-to-tealium-product.js";
import { getCartRows } from "../../mappers/analytics/monetate/mappers/analytics/monetate/get-cart-rows.js";
import { appendSanitizationItems } from "../../utils/utils/append-sanitization-items.js";
import { CREATE_CART } from "../../operations/mutations/operations/mutations/create-cart.js";
import { ADD_CART_LINE_ITEMS } from "../../operations/mutations/operations/mutations/add-cart-line-items.js";
const useAddToCart = () => {
  const intl = useIntl();
  const { locale, currency, country } = intl;
  const settings = useCartSettings();
  const { apiUrl } = useApiConfig();
  const [cart, { setData }] = useCartStorage();
  const { isMonetateProductDataEnabled } = useFeatureToggles();
  const dispatchTrackEvent = useDispatchTrackEvent();
  const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
  const [createCart, createCartMutation] = useMutation(
    CREATE_CART
  );
  const [addCartLineItems, addCartLineItemsMutation] = useMutation(ADD_CART_LINE_ITEMS);
  const handleTrackErrorTealium = React.useCallback(
    (errorDetails, errorMessage, errorSource) => dispatchTrackErrorEvent(
      () => Promise.resolve({
        Error_Source: errorSource || "Server",
        Error_Code: "Add to cart error",
        Error_Details: errorDetails,
        ...errorMessage ? { Error_Message: errorMessage } : {}
      })
    ),
    [dispatchTrackErrorEvent]
  );
  const handleOnCompleted = React.useCallback(
    (addedLineItemDrafts, data2, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent, isQuantityDiscountSelected) => {
      let result;
      if ("addCartLineItems" in data2) {
        result = data2.addCartLineItems;
      }
      if ("createCart" in data2) {
        result = data2.createCart;
      }
      if (result.validationErrors) {
        const solvableValidationErrors = getSolvableValidationErrors(result.validationErrors);
        if (solvableValidationErrors.length > 0 && onSolvableValidationError) {
          onSolvableValidationError(solvableValidationErrors);
          return;
        }
        const validationErrors = getValidationErrorCodes(result.validationErrors);
        onValidationError && onValidationError(validationErrors);
        if (!result.success) {
          return;
        }
      }
      if (!result.cart) {
        handleTrackErrorTealium(errorsMapping.CART_IS_EMPTY);
        return onError(errorsMapping.CART_IS_EMPTY);
      }
      const { cartHash } = result;
      const { id, numberOfItems, lineItems } = result.cart;
      setData({
        cartId: id,
        cartHash: cartHash || cart?.cartHash || null,
        cartCount: numberOfItems
      });
      const addedLineItems = getAddedLineItems(lineItems, addedLineItemDrafts);
      const addedLineItemsMapped = lineItemDataMapper(addedLineItems, settings, intl);
      const addedVisualLineItems = addedLineItems.map((lineItem) => toVisualLineItem(lineItem, intl, settings, apiUrl)).filter((lineItem) => Boolean(lineItem));
      const dataLayerEvents = mapLineItemToDataLayerEvent(
        DataLayer.DataLayerTypes.EventType.add,
        addedLineItemsMapped,
        id,
        isQuantityDiscountSelected
      );
      const products = mapVisualLineItemProductToTealiumProduct(addedVisualLineItems, true);
      dispatchTrackEvent(
        () => Promise.resolve({
          id: "Event",
          Events_CartAdd: "1",
          Products: products
        })
      );
      dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
      pushRemoveLineItemDataLayerEvent && pushRemoveLineItemDataLayerEvent();
      dispatchMiniCartEvent(
        "added",
        addedVisualLineItems,
        id,
        cartHash || cart?.cartHash || void 0
      );
      onSuccess && onSuccess(addedLineItemsMapped);
      monetate().trackCartContents(
        isMonetateProductDataEnabled ? getCartRows(lineItems, currency) : []
      );
    },
    [
      apiUrl,
      cart?.cartHash,
      currency,
      dispatchTrackEvent,
      handleTrackErrorTealium,
      intl,
      isMonetateProductDataEnabled,
      setData,
      settings
    ]
  );
  const addToCart = React.useCallback(
    async (lineItemDrafts, onSuccess, onValidationError, onError = () => void 0, onSolvableValidationError, sanitizationItems, pushRemoveLineItemDataLayerEvent, isQuantityDiscountSelected, options = {}) => {
      let lineItems = lineItemDrafts.map(mapDraftLineItemToServer).filter(isNotNull);
      if (!lineItems.length) {
        handleTrackErrorTealium("No lineItems found", errorsMapping.SOMETHING_WENT_WRONG);
        return onError(errorsMapping.SOMETHING_WENT_WRONG);
      }
      if (sanitizationItems) {
        lineItems = appendSanitizationItems(lineItems, sanitizationItems);
      }
      let localCartData = null;
      try {
        const localStorageCartData = window.localStorage.getItem(CART_STORAGE_KEY);
        localCartData = isNotNull(localStorageCartData) ? JSON.parse(localStorageCartData) : null;
      } catch (err) {
        const errorMessage = `Failed retrieving cart data from localStorage. Error: ${err}`;
        handleTrackErrorTealium(errorMessage, void 0, "Client");
        console.error(errorMessage);
      }
      const { prescriptionSignatures } = options;
      try {
        const { data: data2, errors } = await (isNotNull(localCartData) && isNotNull(localCartData.cartId) ? addCartLineItems({
          variables: {
            cartInput: {
              cartId: localCartData.cartId,
              lineItems
            }
          },
          context: { forwardHeaders: { "cart-hash": cart.cartHash } }
        }) : createCart({
          variables: {
            cartInput: {
              locale,
              currency,
              country,
              lineItems,
              prescriptionSignatures
            }
          }
        }));
        if (errors) {
          handleTrackErrorTealium(errorsMapping.SOMETHING_WENT_WRONG);
          onError(errorsMapping.SOMETHING_WENT_WRONG);
        }
        if (data2) {
          handleOnCompleted(
            lineItemDrafts,
            data2,
            onError,
            onSuccess,
            onValidationError,
            onSolvableValidationError,
            pushRemoveLineItemDataLayerEvent,
            isQuantityDiscountSelected
          );
        }
      } catch (error2) {
        const errorMessage = `Failed adding line items to cart. Error: ${error2}`;
        console.error(errorMessage);
        handleTrackErrorTealium(errorsMapping.SOMETHING_WENT_WRONG, errorMessage);
        onError(errorsMapping.SOMETHING_WENT_WRONG);
      }
    },
    [
      addCartLineItems,
      cart.cartHash,
      country,
      createCart,
      currency,
      handleOnCompleted,
      handleTrackErrorTealium,
      locale
    ]
  );
  const { called, loading, error, data } = cart.cartId ? addCartLineItemsMutation : createCartMutation;
  let newCart;
  if (data && "createCart" in data) {
    newCart = data.createCart.cart;
  }
  if (data && "addCartLineItems" in data) {
    newCart = data.addCartLineItems.cart;
  }
  return [addToCart, { called, loading, error, cart: newCart }];
};
export {
  useAddToCart
};
//# sourceMappingURL=use-add-to-cart.js.map
